import io.ktor.client.HttpClient
import io.ktor.client.engine.js.Js
import io.ktor.client.plugins.ResponseException
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlin.js.Date

// Talks straight to PostgREST -- no supabase-kt SDK -- the same way sgbowling/bowlingliga do.
// CV_SUPABASE_URL / CV_ANON_KEY come from the generated Config.kt (see build.gradle.kts): a
// production build points them at Supabase Cloud without touching a line of source, while every
// other invocation gets the local `supabase start` stack.
//
// Two hard-won constraints live in this file. Breaking either brings back bugs that cost a day:
//
// 1) ktor-client-js 2.3.12 crashed on reading ANY response body on this Kotlin 2.0.20 + Kotlin/JS
//    combination ("Cannot read properties of undefined (reading 'execute_1mk68n_k$')" deep inside
//    ktor-client-core). The fix was Ktor 3.0.3 -- see composeApp/build.gradle.kts, which also
//    records why not 3.5.0.
//
// 2) Even on 3.0.3, `setBody(mapOf(...))` with a heterogeneous map (String/Double/Int/Boolean
//    values mixed) through ContentNegotiation is unreliable: identically shaped calls in one run
//    would alternately succeed and fail with "IllegalArgumentException: Star projections in type
//    arguments are not allowed". So request bodies are assembled by hand as JsonObject/JsonArray
//    (both have concrete, non-generic serializers) and sent as a plain string, bypassing
//    ContentNegotiation on the way out. Responses are likewise decoded by hand from bodyAsText()
//    rather than through `.body<T>()`. Use jsonBody()/jsonArrayBody() and decode() consistently,
//    including where setBody(mapOf(...)) currently "seems" to work.

val httpClient = HttpClient(Js) {
    expectSuccess = true
    install(ContentNegotiation) {
        json(Json { ignoreUnknownKeys = true })
    }
}

private val apiJson = Json { ignoreUnknownKeys = true }

/**
 * PostgREST limits one response to a server-configured maximum (1000 rows in Supabase by
 * default). History-style endpoints must explicitly walk every page; chart statistics avoid this
 * helper entirely and use the database aggregates returned by cv_statistics.
 */
private const val READ_PAGE_SIZE = 1000

private suspend fun <T> fetchAllPages(fetchPage: suspend (offset: Int, limit: Int) -> List<T>): List<T> {
    val result = mutableListOf<T>()
    var offset = 0
    do {
        val page = fetchPage(offset, READ_PAGE_SIZE)
        result += page
        offset += page.size
    } while (page.size == READ_PAGE_SIZE)
    return result
}

/** Manual response decoding -- see note 2 above for why not `.body<T>()`. */
private suspend inline fun <reified T> HttpResponse.decode(): T = apiJson.decodeFromString(bodyAsText())

private fun jsonObjectOf(vararg pairs: Pair<String, JsonElement>) = JsonObject(pairs.toMap())

/** Hand-assembled request body -- see note 2 above. Pair with `contentType(Application.Json)`. */
private fun jsonBody(vararg pairs: Pair<String, JsonElement>): String =
    apiJson.encodeToString(JsonObject.serializer(), jsonObjectOf(*pairs))

/** Same, for PostgREST's bulk endpoints, which take an array of identically shaped objects. */
private fun jsonArrayBody(rows: List<JsonObject>): String =
    apiJson.encodeToString(JsonArray.serializer(), JsonArray(rows))

private fun jp(value: String) = JsonPrimitive(value)
private fun jp(value: Double) = JsonPrimitive(value)
private fun jp(value: Int) = JsonPrimitive(value)
private fun jp(value: Boolean) = JsonPrimitive(value)

/** True for HTTP 401, i.e. the token is gone or expired and the session should be dropped. */
fun Throwable.isUnauthorized(): Boolean =
    (this as? ResponseException)?.response?.status?.value == 401

@Serializable
private data class ApiErrorBody(val message: String? = null)

/** Extracts the API's own error message instead of Ktor's verbose default. */
suspend fun Throwable.apiMessage(): String {
    val bodyText = (this as? ResponseException)?.response?.bodyAsText() ?: return message ?: "neznámá chyba"
    return try {
        apiJson.decodeFromString<ApiErrorBody>(bodyText).message ?: bodyText
    } catch (_: Throwable) {
        bodyText
    }
}

/** PostgREST auth: anonymous (public reads only) or the redeem-invite JWT (reads and writes). */
private fun HttpRequestBuilder.cvAuth(token: String?) {
    header("apikey", CV_ANON_KEY)
    header(HttpHeaders.Authorization, "Bearer ${token ?: CV_ANON_KEY}")
}

private fun HttpRequestBuilder.jsonRequest(token: String?, body: String) {
    cvAuth(token)
    contentType(ContentType.Application.Json)
    setBody(body)
}

// ---------------------------------------------------------------------------------------------
// DTOs -- mirror supabase/migrations/*.sql. Column names there are Czech (they predate this app
// and are live in production); @SerialName keeps that mapping in one place so Kotlin stays English.
// ---------------------------------------------------------------------------------------------

@Serializable
data class Participant(val id: String, val name: String, val active: Boolean = true)

@Serializable
data class ExerciseType(
    val id: String,
    val key: String,
    val name: String,
    @SerialName("unit_label") val unitLabel: String,
    /** How many units make one point ("norma" in the UI): 30 push-ups, 8 km, 1 plank set. */
    @SerialName("points_divisor") val pointsDivisor: Double,
    val active: Boolean = true,
    @SerialName("sort_order") val sortOrder: Int = 0,
)

@Serializable
data class RatingTier(
    val id: String,
    val label: String,
    @SerialName("min_points") val minPoints: Double,
    /** Whether reaching this tier makes the day count towards streaks and completed weeks. */
    @SerialName("counts_as_completed") val countsAsCompleted: Boolean,
    /** Whether this tier gets a streak counter of its own (PLAN-STREAK.md ch. 2.1). */
    @SerialName("tracks_streak") val tracksStreak: Boolean = false,
    @SerialName("sort_order") val sortOrder: Int = 0,
)

@Serializable
data class Entry(
    @SerialName("participant_id") val participantId: String,
    @SerialName("exercise_type_id") val exerciseTypeId: String,
    @SerialName("entry_date") val entryDate: String,
    @SerialName("raw_value") val rawValue: Double,
    val points: Double,
)

@Serializable
data class DailySummary(
    @SerialName("participant_id") val participantId: String,
    @SerialName("entry_date") val entryDate: String,
    @SerialName("body_dnes") val points: Double,
    @SerialName("tier_label") val tierLabel: String? = null,
    @SerialName("counts_as_completed") val countsAsCompleted: Boolean = false,
)

@Serializable
data class Illness(
    val id: String,
    @SerialName("participant_id") val participantId: String,
    @SerialName("started_on") val startedOn: String,
    @SerialName("ended_on") val endedOn: String? = null,
) {
    fun includes(date: String): Boolean = date >= startedOn && (endedOn == null || date <= endedOn)
}

@Serializable
data class Challenge(
    val id: String,
    val name: String,
    val description: String = "",
    @SerialName("starts_on") val startsOn: String,
    @SerialName("ends_on") val endsOn: String,
    @SerialName("period_kind") val periodKind: String,
    val status: String,
    val definition: JsonObject,
    @SerialName("finalize_at") val finalizeAt: String? = null,
)

/** Values collected by the admin form; Api turns them into the stable v1 JSON envelope. */
data class ChallengeDraft(
    val name: String,
    val description: String,
    val startsOn: String,
    val endsOn: String,
    val periodKind: String,
    val metricKind: String,
    val exerciseTypeIds: List<String>,
    val ratingTierId: String? = null,
    val dailyPoints: Double? = null,
    /** Numeric targets for every metric except rating_on_weekdays. Empty means disabled. */
    val medalTargets: Map<String, Double> = emptyMap(),
    /** Rating-tier targets for rating_on_weekdays. Empty means disabled. */
    val medalTierIds: Map<String, String> = emptyMap(),
)

@Serializable
data class ChallengeProgress(
    @SerialName("challenge_id") val challengeId: String,
    @SerialName("challenge_name") val challengeName: String,
    val description: String = "",
    @SerialName("starts_on") val startsOn: String,
    @SerialName("ends_on") val endsOn: String,
    @SerialName("period_kind") val periodKind: String,
    @SerialName("challenge_status") val challengeStatus: String,
    @SerialName("finalize_at") val finalizeAt: String? = null,
    @SerialName("participant_id") val participantId: String,
    @SerialName("participant_name") val participantName: String,
    val medal: String? = null,
    val progress: Double,
    @SerialName("next_medal") val nextMedal: String? = null,
    @SerialName("next_target") val nextTarget: Double? = null,
    @SerialName("progress_text") val progressText: String,
    val details: JsonObject = JsonObject(emptyMap()),
    @SerialName("poradi") val rank: Long,
    @SerialName("is_final") val isFinal: Boolean,
)

@Serializable
data class MedalTotals(
    @SerialName("participant_id") val participantId: String,
    val gold: Long = 0,
    val silver: Long = 0,
    val bronze: Long = 0,
)

@Serializable
data class PeriodMedalTotals(
    @SerialName("period_kind") val periodKind: String,
    @SerialName("starts_on") val startsOn: String,
    @SerialName("ends_on") val endsOn: String,
    @SerialName("participant_id") val participantId: String,
    val gold: Long = 0,
    val silver: Long = 0,
    val bronze: Long = 0,
)

@Serializable
data class LeaderboardRow(
    @SerialName("participant_id") val participantId: String,
    @SerialName("body_celkem") val totalPoints: Double,
    @SerialName("poradi") val rank: Long,
)

/** Aggregated rows returned by cv_statistics; each list is chart-sized, never raw history. */
@Serializable
data class StatisticsExercisePoints(
    @SerialName("exercise_type_id") val exerciseTypeId: String,
    val points: Double,
)

@Serializable
data class StatisticsTrendPoint(val bucket: String, val points: Double)

@Serializable
data class StatisticsTierCount(val label: String, val days: Long)

@Serializable
data class StatisticsCompositionPoint(
    @SerialName("participant_id") val participantId: String,
    @SerialName("exercise_type_id") val exerciseTypeId: String,
    val points: Double,
)

@Serializable
data class StatisticsSnapshot(
    @SerialName("entry_count") val entryCount: Long,
    @SerialName("exercise_points") val exercisePoints: List<StatisticsExercisePoints> = emptyList(),
    val trend: List<StatisticsTrendPoint> = emptyList(),
    @SerialName("tier_counts") val tierCounts: List<StatisticsTierCount> = emptyList(),
    @SerialName("participant_composition")
    val participantComposition: List<StatisticsCompositionPoint> = emptyList(),
)

/** One tracked tier's own counter. Levels are nested: a day at ARCIBOREC continues BOREC too. */
@Serializable
data class StreakLevel(
    @SerialName("tier_id") val tierId: String,
    val label: String,
    @SerialName("min_points") val minPoints: Double,
    val streak: Int,
    val best: Int,
)

@Serializable
data class Streak(
    @SerialName("participant_id") val participantId: String,
    /** The base level's run, kept because it is what "the streak" meant before levels existed. */
    @SerialName("current_streak") val currentStreak: Int,
    @SerialName("best_streak") val bestStreak: Int,
    @SerialName("last_completed_date") val lastCompletedDate: String? = null,
    /** A freeze was spent and the streak dies unless today reaches [repayMinLevel]. */
    val pending: Boolean = false,
    /** [pending] as it stood before today -- what [streakPreview] starts from. */
    @SerialName("pending_through_yesterday") val pendingThroughYesterday: Boolean = false,
    @SerialName("freeze_balance") val freezeBalance: Int = 0,
    /** Completed days banked towards the next freeze, out of [freezeEarnDays]. */
    @SerialName("freeze_progress") val freezeProgress: Int = 0,
    @SerialName("frozen_days_total") val frozenDaysTotal: Int = 0,
    @SerialName("freeze_repaid_total") val freezeRepaidTotal: Int = 0,
    @SerialName("freeze_enabled") val freezeEnabled: Boolean = true,
    @SerialName("freeze_earn_days") val freezeEarnDays: Int = 7,
    @SerialName("freeze_repay_enabled") val freezeRepayEnabled: Boolean = true,
    @SerialName("repay_min_level") val repayMinLevel: Int = 0,
    @SerialName("repay_min_label") val repayMinLabel: String? = null,
    /** How many tracked levels today's points have reached so far. */
    @SerialName("today_level") val todayLevel: Int = 0,
    @SerialName("today_points") val todayPoints: Double = 0.0,
    /** Every tracked level including the ones sitting at zero, today applied. */
    val levels: List<StreakLevel> = emptyList(),
    @SerialName("levels_through_yesterday") val levelsThroughYesterday: List<StreakLevel> = emptyList(),
)

/** One day's verdict from the ledger. Days with no entry are here too -- that is the point. */
@Serializable
data class StreakDay(
    @SerialName("participant_id") val participantId: String,
    val day: String,
    val points: Double,
    val level: Int,
    /** 'completed' | 'frozen' | 'missed' | 'today' */
    val status: String,
    val repaid: Boolean = false,
    val pending: Boolean = false,
    @SerialName("tier_label") val tierLabel: String? = null,
)

@Serializable
data class StreakConfig(
    @SerialName("freeze_enabled") val freezeEnabled: Boolean,
    @SerialName("freeze_earn_days") val freezeEarnDays: Int,
    @SerialName("freeze_max") val freezeMax: Int,
    @SerialName("freeze_repay_enabled") val freezeRepayEnabled: Boolean,
    @SerialName("freeze_repay_min_level") val freezeRepayMinLevel: Int,
)

@Serializable
data class CompletedWeeks(
    @SerialName("participant_id") val participantId: String,
    @SerialName("completed_weeks_streak") val completedWeeksStreak: Int,
    @SerialName("completed_weeks_total") val completedWeeksTotal: Int,
)

@Serializable
private data class RedeemInviteResponse(
    @SerialName("access_token") val accessToken: String? = null,
    val message: String? = null,
)

/** One row to write, as produced by [EntryEdits.changes]. */
data class EntryWrite(val key: EntryKey, val rawValue: Double, val points: Double)

object Api {
    private val restUrl = "$CV_SUPABASE_URL/rest/v1"

    /** Swaps the shared link's invite token for a write-capable JWT (see Session.kt). */
    suspend fun redeemInvite(inviteToken: String): String {
        val response: RedeemInviteResponse =
            httpClient.post("$CV_SUPABASE_URL/functions/v1/redeem-invite") {
                header("apikey", CV_ANON_KEY)
                contentType(ContentType.Application.Json)
                setBody(jsonBody("token" to jp(inviteToken)))
            }.decode()
        return response.accessToken ?: throw IllegalStateException(response.message ?: "redeem-invite selhalo")
    }

    // ---- participants ----

    suspend fun fetchParticipants(includeInactive: Boolean = false): List<Participant> =
        httpClient.get("$restUrl/cv_participant") {
            cvAuth(null)
            parameter("select", "id,name,active")
            if (!includeInactive) parameter("active", "eq.true")
            parameter("order", "name")
        }.decode()

    suspend fun fetchParticipant(id: String): Participant? {
        val rows: List<Participant> = httpClient.get("$restUrl/cv_participant") {
            cvAuth(null)
            parameter("select", "id,name,active")
            parameter("id", "eq.$id")
        }.decode()
        return rows.firstOrNull()
    }

    suspend fun createParticipant(token: String, name: String): Participant {
        // PostgREST answers `Prefer: return=representation` with an array even when the request
        // body was a single bare object.
        val created: List<Participant> = httpClient.post("$restUrl/cv_participant") {
            jsonRequest(token, jsonBody("name" to jp(name)))
            header("Prefer", "return=representation")
        }.decode()
        return created.first()
    }

    suspend fun setParticipantActive(token: String, id: String, active: Boolean) {
        httpClient.patch("$restUrl/cv_participant?id=eq.$id") {
            jsonRequest(token, jsonBody("active" to jp(active)))
        }
    }

    // ---- exercise types & rating tiers ----

    suspend fun fetchExerciseTypes(includeInactive: Boolean = false): List<ExerciseType> =
        httpClient.get("$restUrl/cv_exercise_type") {
            cvAuth(null)
            parameter("select", "id,key,name,unit_label,points_divisor,active,sort_order")
            if (!includeInactive) parameter("active", "eq.true")
            parameter("order", "sort_order")
        }.decode()

    suspend fun fetchRatingTiers(): List<RatingTier> =
        httpClient.get("$restUrl/cv_rating_tier") {
            cvAuth(null)
            parameter("select", "id,label,min_points,counts_as_completed,tracks_streak,sort_order")
            parameter("order", "sort_order")
        }.decode()

    // ---- entries ----

    /** Entries for one day, optionally narrowed to one participant, to prefill the editors. */
    suspend fun fetchEntries(token: String, date: String, participantId: String? = null): List<Entry> =
        httpClient.get("$restUrl/cv_entry") {
            cvAuth(token)
            parameter("select", "participant_id,exercise_type_id,entry_date,raw_value,points")
            parameter("entry_date", "eq.$date")
            participantId?.let { parameter("participant_id", "eq.$it") }
        }.decode()

    /**
     * Writes every changed cell of one day in a single request. PostgREST takes an array body for
     * bulk upserts, which matters here: the grid can touch participants x exercises cells, and one
     * request each would mean dozens of round trips on a phone.
     */
    suspend fun upsertEntries(
        token: String,
        date: String,
        writes: List<EntryWrite>,
        updatedByParticipantId: String,
    ) {
        if (writes.isEmpty()) return
        // `updated_at` has a DB default, but ON CONFLICT DO UPDATE only touches columns present in
        // the request body, so overwriting an existing entry would otherwise keep the old stamp.
        val now = Date().toISOString()
        val rows = writes.map { write ->
            jsonObjectOf(
                "participant_id" to jp(write.key.participantId),
                "exercise_type_id" to jp(write.key.exerciseTypeId),
                "entry_date" to jp(date),
                "raw_value" to jp(write.rawValue),
                "points" to jp(write.points),
                "updated_by_participant_id" to jp(updatedByParticipantId),
                "updated_at" to jp(now),
            )
        }
        httpClient.post("$restUrl/cv_entry") {
            jsonRequest(token, jsonArrayBody(rows))
            // Without `on_conflict`, "resolution=merge-duplicates" resolves against the primary key
            // (`id`, a fresh UUID that never collides) and the insert then trips the REAL unique
            // constraint on (participant_id, exercise_type_id, entry_date).
            parameter("on_conflict", "participant_id,exercise_type_id,entry_date")
            header("Prefer", "resolution=merge-duplicates")
        }
    }

    /** Removes the given exercises for one participant and day (a cleared input means "delete"). */
    suspend fun deleteEntries(token: String, date: String, participantId: String, exerciseTypeIds: Collection<String>) {
        if (exerciseTypeIds.isEmpty()) return
        httpClient.delete("$restUrl/cv_entry") {
            cvAuth(token)
            parameter("entry_date", "eq.$date")
            parameter("participant_id", "eq.$participantId")
            parameter("exercise_type_id", "in.(${exerciseTypeIds.joinToString(",")})")
        }
    }

    // ---- illness ----

    suspend fun fetchIllnesses(token: String, participantId: String? = null): List<Illness> =
        httpClient.get("$restUrl/cv_illness") {
            cvAuth(token)
            parameter("select", "id,participant_id,started_on,ended_on")
            participantId?.let { parameter("participant_id", "eq.$it") }
            parameter("order", "started_on.desc")
        }.decode()

    suspend fun reportIllness(token: String, participantId: String, startedOn: String) {
        httpClient.post("$restUrl/cv_illness") {
            jsonRequest(
                token,
                jsonBody(
                    "participant_id" to jp(participantId),
                    "started_on" to jp(startedOn),
                ),
            )
        }
    }

    suspend fun endIllness(token: String, illnessId: String, endedOn: String) {
        httpClient.patch("$restUrl/cv_illness?id=eq.$illnessId") {
            jsonRequest(token, jsonBody("ended_on" to jp(endedOn)))
        }
    }

    suspend fun updateIllness(token: String, illnessId: String, startedOn: String, endedOn: String?) {
        httpClient.patch("$restUrl/cv_illness?id=eq.$illnessId") {
            jsonRequest(
                token,
                jsonBody(
                    "started_on" to jp(startedOn),
                    "ended_on" to (endedOn?.let(::jp) ?: JsonNull),
                ),
            )
        }
    }

    suspend fun deleteIllness(token: String, illnessId: String) {
        httpClient.delete("$restUrl/cv_illness?id=eq.$illnessId") { cvAuth(token) }
    }

    // ---- challenges ----

    suspend fun fetchChallenges(token: String): List<Challenge> =
        httpClient.get("$restUrl/cv_challenge") {
            cvAuth(token)
            parameter("select", "id,name,description,starts_on,ends_on,period_kind,status,definition,finalize_at")
            parameter("order", "starts_on.desc")
        }.decode()

    suspend fun createChallenge(token: String, draft: ChallengeDraft) {
        val metricPairs = mutableListOf<Pair<String, JsonElement>>(
            "kind" to jp(draft.metricKind),
            "exercise_type_ids" to JsonArray(draft.exerciseTypeIds.map(::jp)),
        )
        draft.ratingTierId?.let { metricPairs += "rating_tier_id" to jp(it) }
        draft.dailyPoints?.let { metricPairs += "daily_points" to jp(it) }
        if (draft.metricKind == "rating_on_weekdays") {
            metricPairs += "weekdays" to JsonArray(listOf(jp(6), jp(7)))
            metricPairs += "require_all_days" to jp(true)
        }

        val medals = linkedMapOf<String, JsonElement>()
        listOf("bronze", "silver", "gold").forEach { medal ->
            val target = if (draft.metricKind == "rating_on_weekdays") {
                draft.medalTierIds[medal]?.let { tierId ->
                    jsonObjectOf("kind" to jp("rating_tier"), "rating_tier_id" to jp(tierId))
                }
            } else {
                draft.medalTargets[medal]?.let { value ->
                    val targetKind = if (draft.metricKind in listOf("rating_days_count", "daily_exercise_minimum")) {
                        "count"
                    } else {
                        "points"
                    }
                    jsonObjectOf("kind" to jp(targetKind), "value" to jp(value))
                }
            }
            target?.let { medals[medal] = jsonObjectOf("target" to it) }
        }

        val definition = jsonObjectOf(
            "schema_version" to jp(1),
            "metric" to JsonObject(metricPairs.toMap()),
            "medals" to JsonObject(medals),
        )
        httpClient.post("$restUrl/cv_challenge") {
            jsonRequest(
                token,
                jsonBody(
                    "name" to jp(draft.name),
                    "description" to jp(draft.description),
                    "starts_on" to jp(draft.startsOn),
                    "ends_on" to jp(draft.endsOn),
                    "period_kind" to jp(draft.periodKind),
                    "status" to jp("published"),
                    "definition" to definition,
                ),
            )
        }
    }

    suspend fun deleteChallenge(token: String, id: String) {
        httpClient.delete("$restUrl/cv_challenge?id=eq.$id") { cvAuth(token) }
    }

    suspend fun updateChallengeText(token: String, id: String, name: String, description: String) {
        httpClient.patch("$restUrl/cv_challenge?id=eq.$id") {
            jsonRequest(token, jsonBody("name" to jp(name), "description" to jp(description)))
        }
    }

    suspend fun finishChallenge(token: String, id: String) {
        httpClient.post("$restUrl/rpc/cv_finish_challenge") {
            jsonRequest(token, jsonBody("p_challenge_id" to jp(id)))
        }
    }

    suspend fun fetchChallengeProgress(token: String, includeFinished: Boolean = true): List<ChallengeProgress> =
        httpClient.post("$restUrl/rpc/cv_challenge_progress") {
            jsonRequest(token, jsonBody("p_include_finished" to jp(includeFinished)))
        }.decode()

    suspend fun fetchMedalTotals(token: String, participantId: String? = null): List<MedalTotals> =
        httpClient.get("$restUrl/cv_challenge_medal_totals") {
            cvAuth(token)
            parameter("select", "participant_id,gold,silver,bronze")
            participantId?.let { parameter("participant_id", "eq.$it") }
        }.decode()

    suspend fun fetchPeriodMedalTotals(token: String, fromDate: String): List<PeriodMedalTotals> =
        httpClient.get("$restUrl/cv_challenge_medal_period_totals") {
            cvAuth(token)
            parameter("select", "period_kind,starts_on,ends_on,participant_id,gold,silver,bronze")
            parameter("starts_on", "gte.$fromDate")
        }.decode()

    // ---- derived views: summaries, leaderboard, streaks, weeks (SPECIFIKACE.md ch. 6) ----

    /** Daily points/rating over a date range; the weekly and monthly boards aggregate this. */
    suspend fun fetchDailySummary(
        token: String,
        participantId: String? = null,
        fromDate: String? = null,
        toDate: String? = null,
    ): List<DailySummary> =
        fetchAllPages { offset, limit ->
            httpClient.get("$restUrl/cv_daily_summary") {
                cvAuth(token)
                parameter("select", "participant_id,entry_date,body_dnes,tier_label,counts_as_completed")
                participantId?.let { parameter("participant_id", "eq.$it") }
                fromDate?.let { parameter("entry_date", "gte.$it") }
                toDate?.let { parameter("entry_date", "lte.$it") }
                parameter("order", "entry_date,participant_id")
                parameter("limit", limit)
                parameter("offset", offset)
            }.decode()
        }

    suspend fun fetchLeaderboard(token: String, participantId: String? = null): List<LeaderboardRow> =
        httpClient.get("$restUrl/cv_leaderboard") {
            cvAuth(token)
            parameter("select", "participant_id,body_celkem,poradi")
            participantId?.let { parameter("participant_id", "eq.$it") }
            parameter("order", "poradi")
        }.decode()

    /** All four Statistics charts, aggregated by PostgreSQL for the requested slice. */
    suspend fun fetchStatistics(
        token: String,
        period: String,
        participantId: String?,
        today: String,
    ): StatisticsSnapshot {
        val parameters = mutableListOf<Pair<String, JsonElement>>(
            "p_period" to jp(period),
            "p_today" to jp(today),
        )
        participantId?.let { parameters += "p_participant_id" to jp(it) }

        val rows: List<StatisticsSnapshot> = httpClient.post("$restUrl/rpc/cv_statistics") {
            jsonRequest(token, jsonBody(*parameters.toTypedArray()))
        }.decode()
        return rows.single()
    }

    suspend fun fetchStreaks(token: String, participantId: String? = null): List<Streak> =
        httpClient.get("$restUrl/cv_streaks") {
            cvAuth(token)
            // No explicit select: cv_streaks is entirely about the streak, every column of it is
            // used somewhere, and listing seventeen names here would just be a second place to
            // update whenever one is added.
            participantId?.let { parameter("participant_id", "eq.$it") }
        }.decode()

    /** One participant's day-by-day verdicts, newest first -- including the days they skipped. */
    suspend fun fetchStreakHistory(token: String, participantId: String): List<StreakDay> =
        httpClient.get("$restUrl/cv_streak_history") {
            cvAuth(token)
            parameter("select", "participant_id,day,points,level,status,repaid,pending,tier_label")
            parameter("participant_id", "eq.$participantId")
            parameter("order", "day.desc")
        }.decode()

    suspend fun fetchStreakConfig(): StreakConfig =
        httpClient.get("$restUrl/cv_streak_config") {
            cvAuth(null)
            parameter(
                "select",
                "freeze_enabled,freeze_earn_days,freeze_max,freeze_repay_enabled,freeze_repay_min_level",
            )
        }.decode<List<StreakConfig>>().first()

    /** Any write here rebuilds the whole ledger by trigger, so callers should reload afterwards. */
    suspend fun updateStreakConfig(token: String, config: StreakConfig) {
        httpClient.patch("$restUrl/cv_streak_config?id=eq.true") {
            jsonRequest(
                token,
                jsonBody(
                    "freeze_enabled" to jp(config.freezeEnabled),
                    "freeze_earn_days" to jp(config.freezeEarnDays),
                    "freeze_max" to jp(config.freezeMax),
                    "freeze_repay_enabled" to jp(config.freezeRepayEnabled),
                    "freeze_repay_min_level" to jp(config.freezeRepayMinLevel),
                ),
            )
        }
    }

    suspend fun fetchCompletedWeeks(token: String, participantId: String? = null): List<CompletedWeeks> =
        httpClient.get("$restUrl/cv_completed_weeks") {
            cvAuth(token)
            parameter("select", "participant_id,completed_weeks_streak,completed_weeks_total")
            participantId?.let { parameter("participant_id", "eq.$it") }
        }.decode()

    // ---- admin (SPECIFIKACE.md ch. 8 point 6) ----
    // No `Prefer: return=representation` below: every caller reloads the whole list afterwards.

    suspend fun createExerciseType(
        token: String,
        key: String,
        name: String,
        unitLabel: String,
        pointsDivisor: Double,
        sortOrder: Int,
    ) {
        httpClient.post("$restUrl/cv_exercise_type") {
            jsonRequest(
                token,
                jsonBody(
                    "key" to jp(key),
                    "name" to jp(name),
                    "unit_label" to jp(unitLabel),
                    "points_divisor" to jp(pointsDivisor),
                    "sort_order" to jp(sortOrder),
                ),
            )
        }
    }

    suspend fun updateExerciseType(token: String, type: ExerciseType) {
        httpClient.patch("$restUrl/cv_exercise_type?id=eq.${type.id}") {
            jsonRequest(
                token,
                jsonBody(
                    "name" to jp(type.name),
                    "unit_label" to jp(type.unitLabel),
                    "points_divisor" to jp(type.pointsDivisor),
                    "active" to jp(type.active),
                    "sort_order" to jp(type.sortOrder),
                ),
            )
        }
    }

    suspend fun createRatingTier(
        token: String,
        label: String,
        minPoints: Double,
        countsAsCompleted: Boolean,
        tracksStreak: Boolean,
        sortOrder: Int,
    ) {
        httpClient.post("$restUrl/cv_rating_tier") {
            jsonRequest(
                token,
                jsonBody(
                    "label" to jp(label),
                    "min_points" to jp(minPoints),
                    "counts_as_completed" to jp(countsAsCompleted),
                    "tracks_streak" to jp(tracksStreak),
                    "sort_order" to jp(sortOrder),
                ),
            )
        }
    }

    suspend fun updateRatingTier(token: String, tier: RatingTier) {
        httpClient.patch("$restUrl/cv_rating_tier?id=eq.${tier.id}") {
            jsonRequest(
                token,
                jsonBody(
                    "label" to jp(tier.label),
                    "min_points" to jp(tier.minPoints),
                    "counts_as_completed" to jp(tier.countsAsCompleted),
                    "tracks_streak" to jp(tier.tracksStreak),
                    "sort_order" to jp(tier.sortOrder),
                ),
            )
        }
    }

    suspend fun deleteRatingTier(token: String, id: String) {
        httpClient.delete("$restUrl/cv_rating_tier?id=eq.$id") { cvAuth(token) }
    }
}
